import { Controller } from "@hotwired/stimulus"

// Notification Controller
// Handles toast notifications (auto-dismiss) and dropdown notification menu.
// Mark-as-read uses fetch with Turbo Stream accept header.
export default class extends Controller {
  static targets = ["menu", "badge", "list"]
  static values = {
    markReadUrl: { type: String, default: "/staff/notifications_read_status" },
    autoDismiss: { type: Number, default: 0 },
    mode: { type: String, default: "dropdown" }
  }

  connect() {
    if (this.modeValue === "toast") {
      this.initToast()
    } else {
      this.initDropdown()
    }
  }

  disconnect() {
    if (this.modeValue === "toast") {
      if (this.dismissTimeout) clearTimeout(this.dismissTimeout)
    } else {
      document.removeEventListener("click", this.boundClickOutside)
    }
  }

  // ── Toast mode ──────────────────────────────────────────────────

  initToast() {
    this.element.classList.add("opacity-0", "translate-x-full")
    requestAnimationFrame(() => {
      this.element.classList.remove("opacity-0", "translate-x-full")
    })
    if (this.autoDismissValue > 0) {
      this.dismissTimeout = setTimeout(() => this.dismiss(), this.autoDismissValue)
    }
  }

  dismiss() {
    this.element.classList.add("opacity-0", "translate-x-full")
    setTimeout(() => this.element.remove(), 300)
  }

  // ── Dropdown mode ───────────────────────────────────────────────

  initDropdown() {
    this.isOpen = false
    this.boundClickOutside = this.clickOutside.bind(this)
  }

  toggle(event) {
    event.stopPropagation()
    this.isOpen ? this.close() : this.open()
  }

  open() {
    if (!this.hasMenuTarget) return
    this.menuTarget.classList.remove("hidden")
    this.menuTarget.classList.add("flex")
    this.isOpen = true
    document.addEventListener("click", this.boundClickOutside)
    this.markAllAsRead()
  }

  close() {
    if (!this.hasMenuTarget) return
    this.menuTarget.classList.add("hidden")
    this.menuTarget.classList.remove("flex")
    this.isOpen = false
    document.removeEventListener("click", this.boundClickOutside)
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  markAllAsRead() {
    const badge = this.hasBadgeTarget ? this.badgeTarget : document.getElementById("notification_badge")
    if (!badge) return
    const currentCount = parseInt(badge.textContent) || 0
    if (currentCount === 0) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.markReadUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      },
      credentials: "same-origin"
    }).then(response => {
      if (response.ok) {
        badge.classList.add("hidden")
        badge.textContent = "0"
        if (this.hasListTarget) {
          this.listTarget.querySelectorAll(".bg-blue-50\\/50").forEach(el => el.classList.remove("bg-blue-50/50"))
          this.listTarget.querySelectorAll(".w-2.h-2.bg-blue-500").forEach(el => el.remove())
        }
      }
    })
  }
}
